import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, PencilRuler } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EsitoBadge, StatoBadge } from "@/components/Badges";
import { VotePanel } from "@/components/dossier/VotePanel";
import { ContinueExploring } from "@/components/dossier/ContinueExploring";
import { ProposeTheory } from "@/components/dossier/ProposeTheory";
import { ApiError } from "@/components/ApiError";

function Resolution({ d }) {
  const r = d.risoluzione;
  if (!r) return null;
  const titles = d.teorie.filter((t) => r.outcome_ids.includes(t.id)).map((t) => t.titolo);
  return (
    <div data-testid="resolution-box" className="rounded-[18px] border border-emerald-600/30 bg-emerald-50 p-5 text-sm">
      <p className="flex items-center gap-2 font-bold text-emerald-800"><CheckCircle2 size={18} /> Rivelato nel manga: {titles.join(" · ")}</p>
      <p className="mt-2">Capitolo {r.capitolo} · rivelazione del {r.data_rivelazione} · fonte: {r.fonte_canonica}</p>
      <p className="mt-1 text-[var(--luma-muted)]">{r.motivazione}</p>
    </div>
  );
}

function Head({ d, user }) {
  return (
    <header className="max-w-[830px] space-y-4">
      <nav aria-label="Percorso" className="flex flex-wrap items-center gap-x-2 text-[13px] text-[var(--luma-muted)]">
        <Link to="/" className="inline-flex min-h-[36px] items-center hover:text-[var(--luma-action)]" data-testid="breadcrumb-home">Le teorie dal mondo</Link><span aria-hidden="true">/</span>
        <Link to="/misteri" className="inline-flex min-h-[36px] items-center hover:text-[var(--luma-action)]" data-testid="back-to-archive">Tutti i misteri</Link><span aria-hidden="true">/</span>
        <Link to={`/misteri?categoria=${encodeURIComponent(d.categoria)}`} className="inline-flex min-h-[36px] items-center hover:text-[var(--luma-action)]" data-testid="breadcrumb-category">{d.categoria}</Link>
      </nav>
      <div className="flex flex-wrap items-center gap-2">
        <span className="luma-eyebrow">{d.categoria}</span>
        {d.esito !== "aperto" && <EsitoBadge esito={d.esito} />}
        {d.stato !== "pubblicato" && <StatoBadge stato={d.stato} />}
      </div>
      <h1 data-testid="dossier-title" className="text-[34px] font-black leading-[1.08] sm:text-5xl">{d.titolo}</h1>
      {d.intro && <p data-testid="dossier-intro" className="text-[17px] leading-relaxed text-[var(--luma-muted)] sm:text-lg">{d.intro}</p>}
      {d.stato !== "pubblicato" && <p className="text-sm text-amber-300">Anteprima riservata al Fondatore: questo dossier è in {d.stato}.</p>}
      {user?.role === "admin" && (
        <Link to={`/admin/dossier/${d.id}`} data-testid="dossier-admin-edit-link" className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-bold text-[var(--luma-action)] hover:underline">
          <PencilRuler size={15} /> Modifica dossier
        </Link>
      )}
      {d.closed_at && d.esito === "chiuso" && <p className="text-sm text-[var(--luma-action)]">Scelte chiuse il {fmtDate(d.closed_at)}.</p>}
    </header>
  );
}

export default function Dossier() {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => api.get(`/dossiers/${id}`).then((r) => { setD(r.data); setError(""); })
    .catch((e) => setError(e.response?.status === 404 ? errMsg(e) : "network")), [id]);
  useEffect(() => { setD(null); setError(""); window.scrollTo(0, 0); load(); }, [load, user]);

  if (error === "network") return <ApiError testid="dossier-api-error" onRetry={() => { setError(""); load(); }} />;
  if (error) return <p data-testid="dossier-error" className="py-20 text-center text-muted-foreground">{error}</p>;
  if (!d) return <p className="py-20 text-center text-muted-foreground">Caricamento…</p>;

  return (
    <div className="space-y-10">
      <Head d={d} user={user} />
      <Resolution d={d} />
      <div className="min-w-0 space-y-10">
        <VotePanel d={d} reload={load} />
        {d.stato === "pubblicato" && <ProposeTheory dossierId={d.id} />}
      </div>
      {d.stato === "pubblicato" && <ContinueExploring id={d.id} />}
    </div>
  );
}
